import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Check, Plus, X, Sparkles, ArrowRight } from "lucide-react";

const RITUAL_IDEAS = ["Kiss my partner goodbye", "Call Mum", "Walk outside", "Gym", "Cold shower", "Phone away at dinner", "Read", "Meditate", "Ten minutes alone"];

function Row({ it, onTap, onRemove, onFull }) {
  return (
    <motion.div layout className="group flex items-center gap-4 py-3.5" data-testid={`routine-item-${it.id}`}>
      <button onClick={() => onTap(it)} aria-label={it.label} data-testid={`routine-check-${it.id}`}
        className={`relative w-7 h-7 rounded-full shrink-0 flex items-center justify-center transition-all duration-700 ${it.completed ? "bg-primary/80 shadow-[0_0_22px_rgba(120,170,230,0.55)]" : "border border-white/20 hover:border-primary/60"}`}>
        {it.completed && <Check className="w-3.5 h-3.5 text-primary-foreground" />}
      </button>
      <button onClick={() => onTap(it)} className="flex-1 min-w-0 text-left">
        <span className={`block text-[15px] transition-colors duration-700 ${it.completed ? "text-foreground/60" : "text-foreground"}`}>{it.label}</span>
        <span className="block text-xs text-muted-foreground truncate">{it.tagline}</span>
      </button>
      {it.simplified && <button onClick={() => onFull(it.id)} className="text-[11px] text-muted-foreground hover:text-primary border border-white/10 rounded-full px-2.5 py-1 shrink-0 transition-colors" data-testid={`routine-full-${it.id}`}>Full version</button>}
      {it.kind === "link" && !it.completed && <ArrowRight className="w-4 h-4 text-muted-foreground" />}
      {it.custom && <button onClick={() => onRemove(it.id)} aria-label="Remove" className="opacity-60 sm:opacity-0 sm:group-hover:opacity-100 text-muted-foreground hover:text-destructive transition-opacity" data-testid={`routine-remove-${it.id}`}><X className="w-4 h-4" /></button>}
    </motion.div>
  );
}

function CoachAdded({ ca, onOpen }) {
  return (
    <motion.button initial={{ opacity: 0 }} animate={{ opacity: 1, boxShadow: ["0 0 0 rgba(120,170,230,0)", "0 0 28px rgba(120,170,230,0.35)", "0 0 0 rgba(120,170,230,0)"] }}
      transition={{ duration: 2.4 }} onClick={() => onOpen(ca)} data-testid="routine-coach-added"
      className="w-full text-left my-2 rounded-2xl border border-primary/30 bg-primary/[0.06] px-4 py-3.5">
      <span className="text-[10px] uppercase tracking-[0.22em] text-primary flex items-center gap-1"><Sparkles className="w-3 h-3" /> Coach added for today</span>
      <span className="flex items-center justify-between gap-3 mt-1">
        <span className="text-[15px]">{ca.context} <span className="text-xs text-muted-foreground">· {ca.duration}</span></span>
        <span className="text-sm text-primary flex items-center gap-1 shrink-0">{ca.label} <ArrowRight className="w-4 h-4" /></span>
      </span>
    </motion.button>
  );
}

export default function MyRoutine({ items, coachAdded, onTap, onOpen, onRemove, onAdd, onFull, grat }) {
  const [adding, setAdding] = useState(false);
  const [label, setLabel] = useState("");
  const firstOpen = items.findIndex((i) => !i.completed);
  const insertAt = coachAdded ? (firstOpen < 0 ? items.length : firstOpen) : -1;
  const done = items.length > 0 && items.every((i) => i.completed);
  const add = (l) => { if (l.trim()) { onAdd(l.trim()); setLabel(""); setAdding(false); } };
  return (
    <section className="mb-12" data-testid="routine-my-routine">
      <h2 className="text-xs uppercase tracking-[0.22em] text-foreground/90 mb-2">My routine</h2>
      <div className="divide-y divide-white/5">
        {items.map((it, i) => (
          <div key={it.id}>
            {i === insertAt && <CoachAdded ca={coachAdded} onOpen={onOpen} />}
            <Row it={it} onTap={onTap} onRemove={onRemove} onFull={onFull} />
            {it.kind === "gratitude" && grat.open && (
              <div className="pb-4 pl-11">
                <Textarea value={grat.text} onChange={(e) => grat.setText(e.target.value)} rows={3} placeholder="Three things I'm grateful for…" data-testid="gratitude-input" />
                <Button size="sm" className="rounded-full mt-2" onClick={grat.save} data-testid="gratitude-save">Save</Button>
              </div>
            )}
          </div>
        ))}
        {insertAt === items.length && <CoachAdded ca={coachAdded} onOpen={onOpen} />}
      </div>
      <AnimatePresence>{done && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="font-serif italic text-foreground/80 mt-4" data-testid="routine-complete">Today's rhythm is complete. Nicely done.</motion.p>
      )}</AnimatePresence>

      <div className="mt-8 rounded-3xl border border-dashed border-white/10 p-5" data-testid="routine-ritual">
        <p className="text-[10px] uppercase tracking-[0.22em] text-primary">One thing that's yours</p>
        <p className="font-serif text-lg mt-1 mb-3">What small thing would make today better?</p>
        {adding ? (
          <>
            <form onSubmit={(e) => { e.preventDefault(); add(label); }} className="flex gap-2 mb-3">
              <Input value={label} onChange={(e) => setLabel(e.target.value)} autoFocus placeholder="Something that's yours…" data-testid="ritual-input" />
              <Button type="submit" className="rounded-full" disabled={!label.trim()} data-testid="ritual-save">Add</Button>
            </form>
            <div className="flex flex-wrap gap-1.5">{RITUAL_IDEAS.map((r) => <button key={r} onClick={() => add(r)} className="px-3 py-1 rounded-full text-xs border border-white/10 text-muted-foreground hover:text-foreground transition-colors" data-testid={`ritual-idea-${r.split(" ")[0].toLowerCase()}`}>{r}</button>)}</div>
          </>
        ) : (
          <button onClick={() => setAdding(true)} className="text-sm text-primary flex items-center gap-1" data-testid="ritual-add-btn"><Plus className="w-4 h-4" /> Add mine</button>
        )}
      </div>
    </section>
  );
}
